import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Upload, FileText, PackageCheck, Truck, Eye, EyeOff } from 'lucide-react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { numOrDefault } from '@/lib/invoiceHelpers';

const inputCls = "text-foreground";
const selectCls = "w-full px-3 py-2 rounded-lg bg-transparent border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50";

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="text-xs text-muted-foreground block mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
  </div>
);

export default function StepEquipmentBanking({ form, set, errors, uploads, setUploads, driverType }) {
  const [showAccount, setShowAccount] = useState(false);
  const isOwnerOperator = driverType === 'Owner Operator';
  const [dispatchFeePercent, setDispatchFeePercent] = useState(3);

  useEffect(() => {
    // Was hardcoded "3%" regardless of what Accounting configured in
    // Pay & Deductions.
    client.entities.CompanySettings.filter({ key: 'dispatch_fee_percent' })
      .then(settings => setDispatchFeePercent(numOrDefault(settings[0]?.value, 3)))
      .catch(() => {});
  }, []);

  const handleVoidCheckUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploads(p => ({ ...p, uploadingVoidCheck: true }));
    toast.info('Uploading document...');
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    set('void_check_url', file_url);
    setUploads(p => ({ ...p, uploadingVoidCheck: false }));
    toast.success('Document uploaded.');
  };

  return (
    <div className="space-y-6">

      {/* Equipment — Owner Operators only */}
      {isOwnerOperator && (
        <>
          <div>
            <h3 className="font-space font-semibold text-foreground text-base">Equipment</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Do you plan to bring your own equipment or lease ours?</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { value: 'own', icon: PackageCheck, title: 'My Own Equipment', desc: 'I have my own truck & trailer.' },
              { value: 'lease', icon: Truck, title: 'Lease from GF Trucklines', desc: "I'd like to lease equipment from the carrier." },
            ].map(({ value, icon: Icon, title, desc }) => {
              const selected = form.equipment_preference === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => set('equipment_preference', value)}
                  className={cn(
                    "text-left p-4 rounded-xl border-2 transition-all space-y-2",
                    selected ? "border-primary bg-primary/10" : "border-border bg-muted/40 hover:border-border"
                  )}
                >
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", selected ? "bg-primary/20" : "bg-white/5")}>
                    <Icon className={cn("w-5 h-5", selected ? "text-primary" : "text-muted-foreground")} />
                  </div>
                  <p className={cn("font-semibold text-sm", selected ? "text-primary" : "text-foreground")}>{title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </button>
              );
            })}
          </div>
          {errors.equipment_preference && <p className="text-xs text-red-400">{errors.equipment_preference}</p>}

          {form.equipment_preference === 'own' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
              <Field label="Truck Type" required>
                <select value={form.truck_type} onChange={e => set('truck_type', e.target.value)} className={cn(selectCls, 'border-input')}>
                  <option value="Dry Van" className="bg-background">Dry Van</option>
                  <option value="Reefer" className="bg-background">Reefer</option>
                </select>
              </Field>
              <div className="grid grid-cols-3 gap-3 sm:col-span-2">
                <Field label="Year" error={errors.truck_year}>
                  <Input value={form.truck_year} onChange={e => set('truck_year', e.target.value)} className={inputCls} placeholder="2024" />
                </Field>
                <Field label="Make" required error={errors.truck_make}>
                  <Input value={form.truck_make} onChange={e => set('truck_make', e.target.value)} className={cn(inputCls, errors.truck_make && 'border-red-500/50')} placeholder="Peterbilt" />
                </Field>
                <Field label="Model" required error={errors.truck_model}>
                  <Input value={form.truck_model} onChange={e => set('truck_model', e.target.value)} className={cn(inputCls, errors.truck_model && 'border-red-500/50')} placeholder="579" />
                </Field>
              </div>
              <Field label="Truck VIN" required error={errors.truck_vin}>
                <Input value={form.truck_vin} onChange={e => set('truck_vin', e.target.value)} className={cn(inputCls, errors.truck_vin && 'border-red-500/50')} />
              </Field>
              <Field label="License Plate">
                <Input value={form.license_plate} onChange={e => set('license_plate', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Plate State">
                <Input value={form.plate_state} onChange={e => set('plate_state', e.target.value.toUpperCase())} maxLength={2} className={inputCls} placeholder="NV" />
              </Field>
              <Field label="Current Odometer">
                <Input type="number" value={form.current_odometer} onChange={e => set('current_odometer', e.target.value)} className={inputCls} placeholder="125000" />
              </Field>
              <Field label="Plate Expiration">
                <Input type="date" value={form.plate_expiration} onChange={e => set('plate_expiration', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Registration Expiration">
                <Input type="date" value={form.registration_expiration} onChange={e => set('registration_expiration', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Annual Inspection Due">
                <Input type="date" value={form.annual_inspection_due} onChange={e => set('annual_inspection_due', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Trailer Info" required error={errors.trailer_info}>
                <Input value={form.trailer_info} onChange={e => set('trailer_info', e.target.value)} className={cn(inputCls, errors.trailer_info && 'border-red-500/50')} placeholder="e.g. 53ft Utility Dry Van" />
              </Field>
            </div>
          )}

          {form.equipment_preference === 'lease' && (
            <div className="px-4 py-3 rounded-xl bg-primary/10 border border-primary/20 text-sm text-primary/90 leading-relaxed">
              Our team will reach out after approval to discuss available trucks, trailers, and lease terms.
            </div>
          )}

          {form.equipment_preference && (
            <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-border bg-muted/40">
              <div>
                <p className="text-sm font-medium text-foreground">Dispatch Service</p>
                <p className="text-xs text-muted-foreground mt-0.5">{dispatchFeePercent}% per load — we handle finding & booking your freight</p>
              </div>
              <button
                type="button"
                onClick={() => set('dispatch_service', !form.dispatch_service)}
                className={cn("relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0", form.dispatch_service ? "bg-primary" : "bg-white/20")}
              >
                <span className={cn("absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200", form.dispatch_service ? "translate-x-5" : "translate-x-0")} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Banking */}
      <div className="pt-3 border-t border-border">
        <h3 className="font-space font-semibold text-foreground text-base">Banking & Direct Deposit</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Used for settlement payments only — stored securely</p>
      </div>

      <div className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs">
        Your banking details are encrypted and used only for direct deposit of your settlement payments.
      </div>

      <div className="space-y-4">
        <Field label="Bank Routing Number" required error={errors.bank_routing_number}>
          <Input value={form.bank_routing_number} onChange={e => set('bank_routing_number', e.target.value)} className={cn(inputCls, errors.bank_routing_number && 'border-red-500/50')} placeholder="9-digit routing number" autoComplete="off" />
        </Field>
        <Field label="Bank Account Number" required error={errors.bank_account_number}>
          <div className="relative">
            <Input
              type={showAccount ? 'text' : 'password'}
              value={form.bank_account_number}
              onChange={e => set('bank_account_number', e.target.value)}
              className={cn(inputCls, 'pr-10', errors.bank_account_number && 'border-red-500/50')}
              placeholder="Account number"
              autoComplete="new-password"
            />
            <button type="button" onClick={() => setShowAccount(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showAccount ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>

        {/* Void check upload */}
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Void Check or Direct Deposit Form</label>
          <label className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors",
            form.void_check_url ? "border-green-500/40 bg-green-500/5" : "border-border bg-muted/40 hover:border-primary/40"
          )}>
            <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleVoidCheckUpload} disabled={uploads.uploadingVoidCheck} />
            {uploads.uploadingVoidCheck ? (
              <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin flex-shrink-0" />
            ) : form.void_check_url ? (
              <FileText className="w-4 h-4 text-green-400 flex-shrink-0" />
            ) : (
              <Upload className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            )}
            <span className={cn("text-sm truncate", form.void_check_url ? "text-green-400" : "text-muted-foreground")}>
              {uploads.uploadingVoidCheck ? 'Uploading…' : form.void_check_url ? 'Uploaded — click to replace' : 'Upload void check or direct deposit form (PDF, JPG, PNG)'}
            </span>
          </label>
          <p className="text-xs text-muted-foreground mt-1">Attach a voided check or your bank's direct deposit authorization form.</p>
        </div>
      </div>
    </div>
  );
}